'use client'

import { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
)

interface RatingDataPoint {
  date: string
  rating: number
}

type Period = '3months' | '6months' | '1year' | 'overall'

const periodOptions: { value: Period; label: string }[] = [
  { value: '3months', label: '3M' },
  { value: '6months', label: '6M' },
  { value: '1year', label: '1Y' },
  { value: 'overall', label: 'All' },
]

interface EntityTrendChartProps {
  entityId: string
  height?: number
}

export function EntityTrendChart({ entityId, height = 60 }: EntityTrendChartProps) {
  const [period, setPeriod] = useState<Period>('3months')
  const [data, setData] = useState<RatingDataPoint[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(`/api/entities/${entityId}/rating-history?period=${period}`)
        if (response.ok) {
          const result = await response.json()
          setData(result.data || [])
        }
      } catch (error) {
        console.error('Error fetching rating history:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [entityId, period])

  const PeriodSelector = () => (
    <div className="flex gap-1 mb-2">
      {periodOptions.map((option) => (
        <button
          key={option.value}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setPeriod(option.value)
          }}
          className={`px-2 py-0.5 text-xs border transition-colors ${
            period === option.value
              ? 'bg-[#1E4289] border-[#FEDB1A] text-[#FEDB1A]'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )

  if (isLoading) {
    return (
      <div>
        <PeriodSelector />
        <div style={{ height }} className="flex items-center justify-center">
          <div className="w-full h-8 bg-slate-700 animate-pulse rounded" />
        </div>
      </div>
    )
  }

  if (data.length < 2) {
    return (
      <div>
        <PeriodSelector />
        <div style={{ height }} className="flex items-center justify-center text-xs text-slate-500">
          Not enough data
        </div>
      </div>
    )
  }

  // Determine trend direction for color
  const firstRating = data[0]?.rating || 0
  const lastRating = data[data.length - 1]?.rating || 0
  const isUp = lastRating >= firstRating
  const lineColor = isUp ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
  const fillColor = isUp ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'

  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        data: data.map(d => d.rating),
        borderColor: lineColor,
        backgroundColor: fillColor,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 8,
        titleFont: { size: 10 },
        bodyFont: { size: 11 },
        callbacks: {
          title: (items: any[]) => {
            if (items.length > 0) {
              return new Date(items[0].label).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              })
            }
            return ''
          },
          label: (context: any) => `Rating: ${context.parsed.y.toFixed(2)}★`,
        },
      },
    },
    scales: {
      x: { display: false },
      y: {
        display: false,
        min: Math.max(0, Math.min(...data.map(d => d.rating)) - 0.5),
        max: Math.min(5, Math.max(...data.map(d => d.rating)) + 0.5),
      },
    },
  }

  return (
    <div>
      <PeriodSelector />
      <div style={{ height }} className="w-full">
        <Line data={chartData} options={options} />
      </div>
    </div>
  )
}

